import { useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';

/**
 * Column count of the product grid at each width, mirroring the `flex-basis`
 * breakpoints of `.searchHitsItem` in `Hits.module.scss` (widest first, so the
 * first match wins). Note the drop back to two columns at 767px, where the
 * facet sidebar appears.
 *
 * Keep this in sync with the stylesheet: the cross-linking band is placed on a
 * row boundary derived from these counts, and a mismatch reintroduces the empty
 * cells it exists to avoid.
 */
const GRID_COLUMNS = [
  { minWidth: 1128, columns: 4 },
  { minWidth: 1000, columns: 3 },
  { minWidth: 767, columns: 2 },
  { minWidth: 600, columns: 3 },
  { minWidth: 374, columns: 2 },
  { minWidth: 0, columns: 1 },
];

export const columnsForWidth = (width: number) =>
  GRID_COLUMNS.find(({ minWidth }) => width >= minWidth)?.columns ?? 1;

/**
 * Number of columns the hits grid is currently rendering. Starts at the
 * single-column value so the first client render matches the server, then
 * settles before paint via a layout effect.
 */
export const useGridColumns = () => {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    const handleResize = debounce(() => setWidth(window.innerWidth), 200);
    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return columnsForWidth(width);
};
